import { StudioCollectionPostOrder } from "@prisma/client";

/** Admin-set default display order for a collection's posts. */
export type CollectionPostSortOrder = "curated" | "newest" | "oldest";

/** Coerce a stored postSortOrder string to a known mode (default curated). */
export function toPostSortOrder(
  value: string | null | undefined,
): CollectionPostSortOrder {
  return value === "newest" || value === "oldest" ? value : "curated";
}

// Effective display order for a collection's posts, shared by the member
// collection page, the post detail up-next rail, and the admin edit modal so
// all surfaces always agree. Modes (StudioCollection.postSortOrder):
//   - "curated" (default): posts with a curated StudioCollectionPostOrder row
//     first by saved index, then posts without a row newest first. A
//     collection with no rows therefore renders newest → oldest.
//   - "newest" / "oldest": pure chronology (publishedAt, falling back to
//     createdAt); curation rows are ignored — "oldest" gives course-style
//     upload order.
export function sortPostsByCollectionOrder<
  T extends {
    id: string;
    publishedAt?: Date | string | null;
    createdAt: Date | string;
  },
>(
  posts: T[],
  postOrder:
    | Pick<StudioCollectionPostOrder, "postId" | "order">[]
    | null
    | undefined,
  sortOrder: CollectionPostSortOrder = "curated",
): T[] {
  const postDate = (post: T): number =>
    new Date(post.publishedAt ?? post.createdAt).getTime();

  if (sortOrder === "newest" || sortOrder === "oldest") {
    return [...posts].sort((a, b) =>
      sortOrder === "newest"
        ? postDate(b) - postDate(a)
        : postDate(a) - postDate(b),
    );
  }

  const orderMap = new Map(
    (postOrder ?? []).map((entry) => [
      entry.postId,
      entry.order ?? Number.MAX_SAFE_INTEGER,
    ]),
  );

  return [...posts].sort((a, b) => {
    const orderA = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const orderB = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }

    return postDate(b) - postDate(a);
  });
}
